/**
 * Sign-in and first-run setup.
 *
 * One form, two modes. The setup mode only appears when the server says no
 * administrator exists yet, and it is the only moment the app will ever create
 * an account.
 */

import { useState } from 'react';
import { MIN_PASSWORD_LENGTH } from '@proxmon/shared';
import { useStore } from '../state/store.ts';

interface Props {
  mode: 'login' | 'setup';
  /** shown when a guest-mode viewer opened the form and can back out of it */
  onCancel?: () => void;
  /** offered on first run when a directory is configured: skip the local account */
  onSwitchToLogin?: () => void;
}

export function AuthScreen({ mode, onCancel, onSwitchToLogin }: Props) {
  const login = useStore((s) => s.login);
  const setup = useStore((s) => s.setup);
  const appName = useStore((s) => s.session?.appName) ?? 'Proxmon';
  const ldapEnabled = useStore((s) => s.session?.ldapEnabled) ?? false;
  const localAccount = useStore((s) => s.session?.localAccountEnabled) ?? false;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (mode === 'setup') {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirm) {
        setError('The two passwords do not match.');
        return;
      }
    }

    setBusy(true);
    try {
      if (mode === 'setup') await setup(username.trim(), password);
      else await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={submit}>
        <h1>{mode === 'setup' ? `Set up ${appName}` : appName}</h1>
        <p className="lede">
          {mode === 'setup'
            ? 'Create the local account. It is the way back in when the directory is unreachable, so give it a password you can find again.'
            : ldapEnabled
              ? 'Sign in with your domain account.'
              : 'Sign in to view the cluster map.'}
        </p>

        {error && <div className="form-error">{error}</div>}

        <div className="field">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            required
            spellCheck={false}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === 'setup' ? 'new-password' : 'current-password'}
            required
          />
          {mode === 'setup' && <div className="hint">At least {MIN_PASSWORD_LENGTH} characters.</div>}
        </div>

        {mode === 'setup' && (
          <div className="field">
            <label htmlFor="confirm">Confirm password</label>
            <input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
        )}

        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Working…' : mode === 'setup' ? 'Create account' : 'Sign in'}
        </button>

        {onCancel && (
          <div className="auth-foot">
            <button type="button" className="btn ghost" onClick={onCancel}>
              Back to the map
            </button>
          </div>
        )}

        {mode === 'login' && ldapEnabled && localAccount && (
          <div className="auth-foot">Directory down? The local account still works.</div>
        )}

        {mode === 'setup' && onSwitchToLogin && (
          <div className="auth-foot">
            <button type="button" className="btn ghost" onClick={onSwitchToLogin}>
              Sign in with a domain account instead
            </button>
          </div>
        )}

        {mode === 'setup' && (
          <div className="auth-foot">
            Forgot it later? Run <code>npm run passwd</code> on the server.
          </div>
        )}
      </form>
    </div>
  );
}
